<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import PrimeError from '@/components/UI/PrimeError.vue';

const inputWrapper = ref<HTMLSpanElement>();

const props = withDefaults(
	defineProps<{
		modelValue: string;
		label?: string;
		variant?: 'input' | 'textarea';
		type?: string;
		id?: string;
		name?: string;
		placeholder?: string;
		required?: boolean;
		disabled?: boolean;
		autofocus?: boolean;
		autoresize?: boolean;
		valid?: boolean;
		errorText?: string;
	}>(),
	{
		label: '',
		type: 'text',
		variant: 'input',
		id: `input-${Math.floor(
			Math.random() * Math.floor(Math.random() * Date.now())
		)}`,
		name: '',
		placeholder: '',
		required: false,
		disabled: false,
		autofocus: false,
		autoresize: false,
		valid: true,
		errorText: '',
	}
);

const emit = defineEmits(['update:modelValue', 'blur', 'paste', 'drop', 'input']);

const value = computed({
	get() {
		return props.modelValue;
	},
	set(value) {
		emit('update:modelValue', value);
	},
});

const autoresizeField = (event: any) => {
	event.target.style.height = 'auto';
	event.target.style.height = event.target.scrollHeight + 'px';
};

onMounted(() => {
	if (props.autofocus) {
		const input = inputWrapper.value?.querySelector(props.variant);
		input?.focus();
	}
});
</script>

<template>
	<div ref="inputWrapper" class="prime-input">
		<label v-if="label" :for="id" v-text="label" />
		<textarea
			v-if="variant === 'textarea'"
			:id="id"
			v-model="value"
			class="prime-input__field prime-input__field--textarea"
			:class="{ 'prime-input__field--invalid': !valid }"
			:name="name"
			:placeholder="placeholder"
			:required="required"
			:disabled="disabled"
			rows="1"
			@input="
				$emit('input', $event);
				autoresizeField($event);
			"
			@blur="$emit('blur', $event)"
			@paste="$emit('paste', $event)"
			@drop="$emit('drop', $event)"
		/>
		<input
			v-else
			:id="id"
			v-model="value"
			class="prime-input__field"
			:class="{ 'prime-input__field--invalid': !valid }"
			:type="type"
			:name="name"
			:placeholder="placeholder"
			:required="required"
			:disabled="disabled"
			@input="$emit('update:modelValue', $event.target?.value || '')"
			@blur="$emit('blur', $event)"
		/>
		<PrimeError v-if="!valid" :message="errorText" />
	</div>
</template>

<style lang="scss">
.prime-input {
	label {
		display: inline-block;
		vertical-align: middle;
		margin-bottom: 0.5rem;
		font-size: var(--font-14);
	}

	&__field {
		display: inline-block;
		vertical-align: middle;
		width: 100%;
		margin-bottom: 1rem;
		border: 1px solid var(--gray-400);
		padding: 0.75rem 0.75rem;
		background-color: var(--white);
		color: var(--text-color);
		font-size: var(--font-16);
		line-height: 1.2;
		font-weight: 400;
		border-radius: var(--border-radius-sm);
		transition: background-color 0.25s, border-color 0.25s, box-shadow 0.25s;

		&:last-child {
			margin-bottom: 0;
		}

		&--textarea {
			height: auto;
			min-height: 85px;
			max-height: 200px;
			overflow: auto;
			resize: vertical;
			transition: height 0.25s linear;
		}

		&::placeholder {
			color: var(--gray-600);
		}

		&:hover:not(:disabled) {
			border-color: var(--primary-color);
		}

		&:focus {
			outline: none;
			outline-offset: 0;
			border-color: var(--primary-color);
			box-shadow: 0 0 0 0.2rem rgba(var(--primary-color-rgb), 0.24);
		}

		&:disabled {
			background-color: var(--gray-100);
		}
	}
}

.prime-input__field--invalid {
	border-color: var(--error-color);
	color: var(--error-color);
	box-shadow: 0 0 0 0.2rem rgba(var(--error-color-rgb), 0.24);

	&:hover:not(:disabled) {
		border-color: var(--error-color);
	}

	&:focus {
		outline: none;
		outline-offset: 0;
		border-color: var(--error-color);
		box-shadow: 0 0 0 0.2rem rgba(var(--error-color-rgb), 0.24);
	}
}
</style>
